// The executive meeting summary as words: the plain text that becomes the
// activity's description and the clipboard's text flavour, the HTML that
// becomes its rich flavour, and the title and subject. Pure functions over
// the card's payload. Section titles are bold; nothing else changes size.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** 2026-10-07 → 7 October 2026. */
export function longDate(day) {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return day || '';
  const [y, m, d] = day.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

const personLine = (p) => (p.title ? `${p.name}, ${p.title}` : p.name);
const included = (list) => (list || []).filter((p) => p.include !== false);
const stepLine = (n) => {
  const bits = [];
  if (n.owner_name) bits.push(n.owner_name);
  if (n.due_date) bits.push(`by ${longDate(n.due_date)}`);
  return bits.length ? `${n.title} — ${bits.join(', ')}` : n.title;
};

// What a customer must not receive — the server's rule, mirrored so the card warns first.
const CUSTOMER_NO = [/\$\s?\d/, /\b(gross|net|profit|our|the)\s+margins?\b/i, /\bmarkup\b/i, /\binternal(ly)?\b/i, /\bcompetitors?\b/i];

/** The first phrase a customer-facing report must not carry, or null. */
export function customerLeak(report) {
  const texts = [report.executive_summary || '', ...(report.highlights || []), ...(report.next_steps || []).map((n) => n.title || ''), report.subject || ''];
  for (const t of texts) {
    const re = CUSTOMER_NO.find((x) => x.test(t));
    if (re) return (t.match(re) || [''])[0];
  }
  return null;
}

/** Our side of the title when the server sent none (an older card). */
export const FALLBACK_COMPANY = 'Active Optics';
export const companyFor = (report) => report.company_name || FALLBACK_COMPANY;

/** "<Account> <> <our company> Meeting Summary | 7 October 2026" — the server's, or built the same way. */
export function titleFor(report) {
  return report.title || `${report.account_name} <> ${companyFor(report)} Meeting Summary | ${longDate(report.meeting_date)}`;
}

export function subjectFor(report) {
  return report.subject || titleFor(report);
}

// Section titles are bold and nothing else changes size: the plain text
// marks them with **, which the CRM's activity views render as bold.
const bullets = (items, empty) => (items.length ? items.map((i) => `• ${i}`).join('\n') : `• ${empty}`);

/** The report as plain text: the activity's description and the clipboard's text flavour. */
export function renderText(report) {
  const customers = included(report.customer_attendees);
  const internals = included(report.internal_attendees);
  return [
    `**${titleFor(report)}**`, '',
    '**Executive summary**', report.executive_summary || '', '',
    '**Attendees**',
    report.account_name, bullets(customers.map(personLine), 'none recorded'),
    companyFor(report), bullets(internals.map((p) => p.name), 'none recorded'), '',
    '**Key highlights**', bullets(report.highlights || [], 'none'), '',
    '**Next steps / action items**', bullets((report.next_steps || []).map(stepLine), 'none'),
  ].join('\n');
}

const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The report as email-safe HTML: one font size, bold section titles, inline styles, nothing fancy. */
export function renderHtml(report) {
  const customers = included(report.customer_attendees);
  const internals = included(report.internal_attendees);
  const h = (text) => `<p style="margin:14px 0 4px;"><strong>${esc(text)}</strong></p>`;
  const line = (text) => `<p style="margin:6px 0 2px;">${esc(text)}</p>`;
  const ul = (items, empty) => (items.length
    ? `<ul style="margin:0;padding-left:20px;">${items.map((i) => `<li style="margin:2px 0;">${esc(i)}</li>`).join('')}</ul>`
    : `<p style="margin:0;color:#666;">${esc(empty)}</p>`);
  const paragraphs = String(report.executive_summary || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    .map((p) => `<p style="margin:0 0 8px;">${esc(p).replace(/\n+/g, '<br>')}</p>`).join('') || '<p style="margin:0;"></p>';
  return [
    '<div style="font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.45;color:#111;">',
    `<p style="margin:0 0 12px;"><strong>${esc(titleFor(report))}</strong></p>`,
    h('Executive summary'), paragraphs,
    h('Attendees'),
    line(report.account_name), ul(customers.map(personLine), 'none recorded'),
    line(companyFor(report)), ul(internals.map((p) => p.name), 'none recorded'),
    h('Key highlights'), ul(report.highlights || [], 'none'),
    h('Next steps / action items'), ul((report.next_steps || []).map(stepLine), 'none'),
    '</div>',
  ].join('');
}

/** Put the report on the clipboard as rich text and plain text together; plain text alone where that is all the browser allows. */
export async function copyReport(report) {
  const text = renderText(report);
  const html = renderHtml(report);
  const clip = typeof navigator !== 'undefined' ? navigator.clipboard : null;
  if (!clip) throw new Error('The clipboard is not available here');
  if (typeof window !== 'undefined' && typeof window.ClipboardItem === 'function' && typeof clip.write === 'function') {
    try {
      await clip.write([new window.ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([text], { type: 'text/plain' }) })]);
      return 'rich';
    } catch (e) { /* fall through to plain text */ }
  }
  await clip.writeText(text);
  return 'plain';
}
